import { ComboBox, InlineLoading, InlineNotification, Select, SelectItem, TextInput } from '@carbon/react';
import { localized, useTranslation } from '@pos/i18n';
import { useEffect, useState } from 'react';
import './Address.scss';
import { EMPTY_ADDRESS } from './emptyAddress';

type Texts = Partial<Record<string, string>>;

export interface AddressLevel {
  level: number;
  code: string;
  name: string;
  nameI18n: Texts;
  isRequired: boolean;
}

export interface AddressCountry {
  code: string;
  name: string;
  nameI18n: Texts;
  levels: readonly AddressLevel[];
}

export interface AddressDivision {
  id: string;
  code: string;
  level: number;
  name: string;
  nameI18n: Texts;
}

/** Where the lists come from: the location master data, at HO or from the store's replica (ADR 0026). */
export interface AddressSource {
  countries(): Promise<readonly AddressCountry[]>;
  divisions(query: {
    country: string;
    level: number;
    parentId?: string;
    limit?: number;
  }): Promise<readonly AddressDivision[]>;
}

/** The first three levels of a country's divisions (province, district, ward in Vietnam) by their codes, and the street. */
export interface AddressValue {
  countryCode: string;
  provinceCode: string;
  districtCode: string;
  wardCode: string;
  postalCode: string;
  street: string;
}

/** The names of what is chosen, for showing and for the snapshot saved with an order. */
export interface AddressNames {
  province: string;
  district: string;
  ward: string;
}

export interface AddressFieldsProps {
  value: AddressValue;
  onChange(value: AddressValue, names: AddressNames): void;
  source: AddressSource;
  /** The id prefix of the fields, unique on the screen. */
  idPrefix: string;
  /** The street must be filled in; the levels the country marks required are marked too. */
  streetRequired?: boolean;
  autoFocusStreet?: boolean;
  /** Told whether the street and every level the country requires are filled in, whenever that changes. */
  onCompleteChange?(complete: boolean): void;
  /** Marks every empty required field invalid, e.g. after a failed save; fields are also marked once left empty. */
  showRequired?: boolean;
}

const CODE_OF = { 1: 'provinceCode', 2: 'districtCode', 3: 'wardCode' } as const;
const NAME_OF = { 1: 'province', 2: 'district', 3: 'ward' } as const;
type Level = keyof typeof CODE_OF;

/** Lower case without accents, so "ba dinh" finds "Ba Đình" (frontend/CLAUDE.md). */
const fold = (text: string) => text.normalize('NFD').replace(/\p{M}/gu, '').replace(/đ/gi, 'd').toLowerCase();

const nameOf = (item: { name: string; nameI18n: Texts }) => localized(item.nameI18n, item.name);

/**
 * The one address form (FR-LOC-04): the country, then each of its levels as a searchable list that follows the one above
 * (province, then ward), then the street and postal code. Every screen that takes an address uses it, so every address is
 * chosen from the same master data. It reads the lists through <see cref="AddressSource"/>, so it works offline at a store.
 */
export function AddressFields({
  value,
  onChange,
  source,
  idPrefix,
  streetRequired,
  autoFocusStreet,
  onCompleteChange,
  showRequired,
}: AddressFieldsProps) {
  const { t } = useTranslation();
  const [countries, setCountries] = useState<readonly AddressCountry[]>();
  const [options, setOptions] = useState<Partial<Record<Level, readonly AddressDivision[]>>>({});
  const [failed, setFailed] = useState(false);
  const [loadedKey, setLoadedKey] = useState<string>();
  // Fields the user has left; an empty required one then shows as invalid.
  const [left, setLeft] = useState<ReadonlySet<string>>(new Set());
  const leave = (field: string) => setLeft((current) => new Set(current).add(field));

  useEffect(() => {
    let live = true;
    source
      .countries()
      .then((list) => live && setCountries(list))
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, [source]);

  // A country that isn't chosen yet is the first one: a business that trades in one country needn't say which.
  const country = countries?.find((c) => c.code === value.countryCode) ?? countries?.[0];
  const levels = (country?.levels ?? []).filter(
    (l): l is AddressLevel & { level: Level } => l.level >= 1 && l.level <= 3,
  );

  // Complete: the street if it is required, and a division for each level the country requires. A store whose list isn't
  // set up yet can't ask for levels, so it asks for the street only.
  const complete =
    (!streetRequired || value.street.trim() !== '') &&
    levels.every((l) => !l.isRequired || value[CODE_OF[l.level]] !== '');
  useEffect(() => {
    onCompleteChange?.(complete);
  }, [complete, onCompleteChange]);

  // Each level's list follows the choice above it; they load in turn as the chain is known.
  const key = `${country?.code ?? ''}|${levels.map((l) => l.level).join(',')}|${value.provinceCode}|${value.districtCode}`;
  const loading = !failed && (!countries || (country !== undefined && loadedKey !== key));
  useEffect(() => {
    if (!country) return;
    let live = true;
    (async () => {
      const next: Partial<Record<Level, readonly AddressDivision[]>> = {};
      let parentId: string | undefined;
      for (const [index, l] of levels.entries()) {
        if (index > 0 && !parentId) {
          next[l.level] = [];
          continue;
        }
        const rows = await source.divisions({ country: country.code, level: l.level, parentId, limit: 1000 });
        next[l.level] = rows;
        parentId = rows.find((r) => r.code === value[CODE_OF[l.level]])?.id;
      }
      if (live) setOptions(next);
    })()
      .catch(() => live && setFailed(true))
      .finally(() => live && setLoadedKey(key));
    return () => {
      live = false;
    };
    // `key` stands for the country, the levels and the codes above the last level.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, source]);

  const names = (v: AddressValue): AddressNames => {
    const pick = (level: Level) => {
      const found = options[level]?.find((d) => d.code === v[CODE_OF[level]]);
      return found ? nameOf(found) : '';
    };
    return { province: pick(1), district: pick(2), ward: pick(3) };
  };

  const choose = (level: Level, division: AddressDivision | null) => {
    // The list also reports a choice it was handed (an address being edited); that is no change, and must not clear the levels below.
    if ((division?.code ?? '') === value[CODE_OF[level]]) return;
    // Likewise when the list lost the choice only because it is still loading the options of a new parent.
    if (!division && value[CODE_OF[level]] && !options[level]?.some((d) => d.code === value[CODE_OF[level]])) return;
    const next: AddressValue = {
      ...value,
      countryCode: country?.code ?? value.countryCode,
      [CODE_OF[level]]: division?.code ?? '',
    };
    // The levels below belonged to the old choice.
    for (const deeper of [1, 2, 3] as const) {
      if (deeper > level) next[CODE_OF[deeper]] = '';
    }
    const pickedNames = names(next);
    if (division) pickedNames[NAME_OF[level]] = nameOf(division);
    for (const deeper of [1, 2, 3] as const) {
      if (deeper > level) pickedNames[NAME_OF[deeper]] = '';
    }
    onChange(next, pickedNames);
  };

  const levelLabel = (l: AddressLevel) => localized(l.nameI18n, l.name);

  return (
    <div className="pos-address">
      {countries && countries.length > 1 && (
        <Select
          id={`${idPrefix}-country`}
          size="lg"
          labelText={t('address.country')}
          value={country?.code ?? ''}
          onChange={(e) =>
            onChange(
              { ...EMPTY_ADDRESS, street: value.street, countryCode: e.target.value },
              { province: '', district: '', ward: '' },
            )
          }
        >
          {countries.map((c) => (
            <SelectItem key={c.code} value={c.code} text={localized(c.nameI18n, c.name)} />
          ))}
        </Select>
      )}
      {loading && !failed && <InlineLoading description={t('common.loading')} />}
      {failed && (
        <InlineNotification
          kind="warning"
          lowContrast
          hideCloseButton
          statusIconDescription={t('carbon.warning')}
          title={t('address.unavailable')}
        />
      )}
      {countries?.length === 0 && (
        <InlineNotification
          kind="info"
          lowContrast
          hideCloseButton
          statusIconDescription={t('carbon.info')}
          title={t('address.notSetUp')}
        />
      )}
      <div className="pos-address__levels">
        {levels.map((l, index) => {
          const items = options[l.level] ?? [];
          const above = levels[index - 1];
          const selected = items.find((d) => d.code === value[CODE_OF[l.level]]) ?? null;
          const waiting = index > 0 && !!above && !value[CODE_OF[above.level]];
          const invalid = l.isRequired && !waiting && !selected && (showRequired || left.has(`level-${l.level}`));
          return (
            // Leaving the list (focus out) marks it as visited; focus events bubble to this wrapper.
            <div key={l.level} onBlur={() => leave(`level-${l.level}`)}>
            <ComboBox
              id={`${idPrefix}-level-${l.level}`}
              size="lg"
              titleText={levelLabel(l)}
              placeholder={t('address.choose')}
              items={items as AddressDivision[]}
              itemToString={(item: AddressDivision | null) => (item ? nameOf(item) : '')}
              selectedItem={selected}
              disabled={waiting}
              required={l.isRequired}
              invalid={invalid}
              invalidText={t('address.required')}
              shouldFilterItem={({ item, inputValue }: { item: AddressDivision; inputValue: string | null }) =>
                !inputValue ||
                // The text of the choice made is not a search: opening the list shows them all.
                (selected !== null && inputValue === nameOf(selected)) ||
                fold(nameOf(item)).includes(fold(inputValue)) ||
                fold(item.name).includes(fold(inputValue))
              }
              onChange={({ selectedItem }: { selectedItem?: AddressDivision | null }) =>
                choose(l.level, selectedItem ?? null)
              }
            />
            </div>
          );
        })}
      </div>
      <TextInput
        id={`${idPrefix}-street`}
        size="lg"
        labelText={t('address.street')}
        required={streetRequired}
        invalid={Boolean(streetRequired) && value.street.trim() === '' && (showRequired || left.has('street'))}
        invalidText={t('address.required')}
        onBlur={() => leave('street')}
        value={value.street}
        autoFocus={autoFocusStreet}
        onChange={(e) =>
          onChange({ ...value, countryCode: country?.code ?? value.countryCode, street: e.target.value }, names(value))
        }
      />
      <TextInput
        id={`${idPrefix}-postal`}
        size="lg"
        labelText={t('address.postalCode')}
        value={value.postalCode}
        onChange={(e) =>
          onChange(
            { ...value, countryCode: country?.code ?? value.countryCode, postalCode: e.target.value },
            names(value),
          )
        }
      />
    </div>
  );
}
